import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { useWelcomeModal } from '../hooks/useWelcomeModal';
import { useOnboardingModal } from '../hooks/useOnboardingModal';
import { WelcomeLetter } from './WelcomeLetter';
import { OnboardingModal } from './OnboardingModal';
import { DashboardCard } from './DashboardCard';
import { Search, BookOpen, Sparkles } from 'lucide-react';
import { SmartSearchPlaceholder } from './SmartSearchPlaceholder';
import { Button } from '@/shared/components/ui/button';
import { CareSeekerLongevityCard } from '@/features/longevity-assessment';
import { MyRequestsSection } from '@/features/consultations/components/MyRequestsSection';
import { PermissionGate } from '@/features/workspace-sub-delegation';
import { BetaBadge } from '@/shared/components/BetaBadge';
import { useConciergeChooser } from '@/features/care-concierge/chooser/ConciergeChooserProvider';

export default function SimplifiedDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showModal, dismissModal } = useWelcomeModal();
  const { showModal: showOnboarding, dismissModal: dismissOnboarding } = useOnboardingModal();
  const { openConsultation } = useConciergeChooser();

  const [showPlaceholder, setShowPlaceholder] = useState(false);
  const [showWelcomeLetter, setShowWelcomeLetter] = useState(false);
  const [showOnboardingManual, setShowOnboardingManual] = useState(false);

  useEffect(() => {
    if (!showModal && !showWelcomeLetter && !showOnboarding && !showOnboardingManual) {
      window.scrollTo(0, 0);
    }
  }, [showModal, showWelcomeLetter, showOnboarding, showOnboardingManual]);

  const handleCardAction = (action: string) => {
    switch (action) {
      case 'matchmaking':
        navigate('/matchmaking');
        break;
      case 'pro-directory':
        navigate('/search-providers');
        break;
      case 'resource-hub':
        navigate('/resources');
        break;
    }
  };

  // Show full-page welcome letter if user hasn't dismissed it (first time) or clicked to view it
  if (showModal) {
    return <WelcomeLetter onClose={dismissModal} />;
  }

  if (showWelcomeLetter) {
    return <WelcomeLetter onClose={() => setShowWelcomeLetter(false)} />;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <h1 className="text-3xl font-bold mb-6 text-gray-900">
          Welcome, {user?.firstName || 'there'}
        </h1>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Feature 025: bQuest Navigator - Care Seeker Access */}
          <DashboardCard
            title={
              <span className="flex items-center justify-center">
                bQuest Navigator
                <BetaBadge />
              </span>
            }
            description="Not sure where to start? Tell us about your situation and we'll match you with the right professionals and resources. Our bQuest Navigator takes the guesswork out of finding help."
            icon={Sparkles}
            buttonText="Try bQuest Navigator"
            onClick={() => navigate('/care-navigator')}
            className="md:col-span-2"
          />

          {/* Feature 104 (Kirkland revamp): this card is the only in-app entry
              to the paid consultation — it opens the Calendly directly. */}
          <div className="md:col-span-2 rounded-2xl border border-gray-200 bg-white p-6 sm:p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              Get expert guidance on what to do next
            </h2>
            <p className="text-sm sm:text-base text-gray-700 mb-2">
              Meet one-on-one with an Aging Life Care expert&mdash;invite family members, get your
              questions answered, and receive a personalized care consultation with clear next
              steps and vetted local providers for your immediate and short-term needs.
            </p>
            <p className="text-sm font-medium text-gray-500 mb-4">
              60-minute paid consultation &middot; $350
            </p>
            <Button
              onClick={() => openConsultation('dashboard-card')}
              className="bg-[#E2A807] hover:bg-[#C99006] text-white"
            >
              Book a Consultation
            </Button>
          </div>

          {/* Feature 095: Longevity Readiness Assessment (above Care Guides) */}
          <CareSeekerLongevityCard />

          {/* Q/A & Care Plans shortcut */}
            <div className="md:col-span-2 rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Your Care Guides</h2>
                <p className="mt-1 text-sm text-gray-600">
                  Review your previous conversations, answers, and saved care guides all in one place.
                </p>
              </div>
              <Button
                onClick={() => navigate('/care-navigator/dashboard')}
                className="flex-shrink-0 bg-[#31786F] hover:bg-[#27675F] text-white"
              >
                Care Guides
              </Button>
            </div>

          {/* Pro Directory - Left Half */}
          <DashboardCard
            title="Pro Directory"
            description="Know what you're looking for? Our Pro Directory simplifies your search with pre-vetted professionals across all aspects of elder care. Filter by specialty, location, and services to quickly connect with experts who can support your unique situation."
            icon={Search}
            buttonText="Find Providers"
            onClick={() => handleCardAction('pro-directory')}
          />

          {/* Resource Hub - Right Half */}
          <DashboardCard
            title="Resource Hub"
            description="The first step towards achieving peace of mind is finding answers to your important questions. The Resource Hub is a curated library of information offering: Articles, Books, Podcasts and Videos. These resources will build your knowledge base and empower you to take the next step."
            icon={BookOpen}
            buttonText="Browse Resources"
            onClick={() => handleCardAction('resource-hub')}
          />
        </div>

        {/* Feature 014: My Requests Section */}
        <div className="mt-12">
          <PermissionGate permission="WORKSPACE_OPERATIONS" feature="requests">
            <MyRequestsSection />
          </PermissionGate>
        </div>

        {/* View Welcome Letter and Platform Overview Buttons */}
        <div className="mt-12 flex justify-center gap-4">
          <Button
            onClick={() => setShowWelcomeLetter(true)}
            variant="outline"
            className="text-[#026857] border-[#026857] hover:bg-[#026857] hover:text-white"
          >
            See Kelly and Lauren's Letter
          </Button>
          <Button
            onClick={() => setShowOnboardingManual(true)}
            variant="outline"
            className="text-[#026857] border-[#026857] hover:bg-[#026857] hover:text-white"
          >
            See Platform Overview
          </Button>
        </div>
      </div>

      {/* Modals */}
      <OnboardingModal
        open={showOnboarding || showOnboardingManual}
        onClose={() => {
          dismissOnboarding();
          setShowOnboardingManual(false);
        }}
      />
      <SmartSearchPlaceholder
        open={showPlaceholder}
        onClose={() => setShowPlaceholder(false)}
      />
    </div>
  );
}
